import { faCircleExclamation } from '@fortawesome/pro-solid-svg-icons';

import Description from '../../components/Description/Description';
import Group from '../../components/Group/Group';
import IconWithText from '../../components/IconWithText/IconWithText';
import MonetaryField from '../../components/MonetaryField/MonetaryField';
import PagesFooter from '../../components/PagesFooter/PagesFooter';
import PagesHeader from '../../components/PagesHeader/PagesHeader';
import Title from '../../components/Title/Title';
import { usePagesContext } from '../../context/PagesContext';
import { scrollToFieldWithError } from '../../helpers/scrollToFieldWithError';
import { useAppDispatch, useAppSelector } from '../../hooks';
import useComponentTracker from '../../hooks/useComponentTracker';
import useDefaultPagesAction from '../../hooks/useDefaultPagesAction';
import { ValidationResultType } from '../../hooks/useFieldValidation';
import usePagesConfig from '../../hooks/usePagesConfig';
import useSetBannerType from '../../hooks/useSetBannerType';
import useTranslation from '../../hooks/useTranslation';
import useValidation from '../../hooks/useValidation';
import { addOrUpdateFormField } from '../../store/applicationform';
import { selectFieldValue } from '../../store/applicationform/applicationFormSelectors';
import { WizardPageAction } from '../../store/wizard/wizard';

const IncomePageNO = () => {
    useComponentTracker('FinancePage');

    const dispatch = useAppDispatch();
    const translate = useTranslation();
    const defaultPagesAction = useDefaultPagesAction();
    const validationErrors = useValidation(['applicantYearlyGrossIncome']);
    const { totalSections, totalAboutYouPages } = usePagesConfig();
    const { currentPage } = usePagesContext();
    const employmentType = useAppSelector(selectFieldValue)('applicantEmploymentType');

    useSetBannerType('bannerOne');

    const minValueValidation = async (value?: string): Promise<ValidationResultType> => {
        const isValid = (value ?? 0) > '0';

        return {
            valid: isValid,
            errorMessageKey: isValid ? undefined : translate('field.applicant.min-value.error'),
        };
    };

    const onAction = async (action: WizardPageAction) => {
        if (action === 'next' && validationErrors.length) {
            scrollToFieldWithError(validationErrors);
            validationErrors.forEach((validationError) => {
                dispatch(addOrUpdateFormField(validationError));
            });
            return false;
        }

        return defaultPagesAction(action);
    };

    return (
        <>
            <PagesHeader
                actions={['back']}
                onAction={onAction}
                currentPage={currentPage}
                currentSection={0}
                numberOfSections={totalSections}
                numberOfPagesInSection={totalAboutYouPages}
                currentPageInSection={currentPage}
                sectionTitle={translate('field.section.about-you')}
            />
            <Title text={translate('page.income.title')} />
            <Description text={translate('page.income.description')} />
            <Group>
                <MonetaryField
                    name="applicantYearlyGrossIncome"
                    label={translate('field.applicant-yearly-gross-income.label')}
                    customValidation={minValueValidation}
                />
                <MonetaryField
                    name="applicantMonthlyIncomeOther"
                    label={translate('field.applicant-monthly-income-other.label')}
                />
            </Group>
            {employmentType === 'PENSIONER' ? (
                <IconWithText icon={faCircleExclamation} text="page.income.body-text" iconSize="sm" />
            ) : null}
            <PagesFooter
                actions={['next']}
                onAction={onAction}
                currentSection={0}
                numberOfSections={totalSections}
                numberOfPagesInSection={totalAboutYouPages}
                currentPageInSection={currentPage}
                sectionTitle={translate('field.section.about-you')}
            />
        </>
    );
};

export default IncomePageNO;
